'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import {
  Users,
  Eye,
  MessageCircle,
  Flame,
  TrendingUp,
  ArrowRight,
  Plus,
  Calendar,
  Activity,
  Building2,
  Loader2,
  BookOpen
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { de } from 'date-fns/locale'

interface Stats {
  totals: {
    memorials: number
    condolences: number
    candles: number
    memories: number
    institutions: number
    familyAccounts: number
  }
  recent: {
    memorialsThisMonth: number
    condolencesThisWeek: number
    candlesThisWeek: number
  }
  activity: Array<{
    type: 'condolence' | 'candle'
    memorial: string
    memorialSlug: string
    user: string
    createdAt: string
  }>
}

interface Company {
  id: string
  name: string
}

interface Memorial {
  id: string
  slug: string
  firstName: string | null
  lastName: string | null
  city: string | null
  dateOfDeath: string | null
  _count: {
    condolenceComments: number
    candles: number
  }
}

export default function AdminDashboardPage() {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<Stats | null>(null)
  const [company, setCompany] = useState<Company | null>(null)
  const [memorials, setMemorials] = useState<Memorial[]>([])

  useEffect(() => {
    async function fetchData() {
      try {
        const [statsRes, companyRes, memorialsRes] = await Promise.all([
          fetch('/api/admin/stats'),
          fetch('/api/admin/company'),
          fetch('/api/admin/memorials'),
        ])

        if (statsRes.ok) {
          const statsData = await statsRes.json()
          setStats(statsData)
        }

        if (companyRes.ok) {
          const companyData = await companyRes.json()
          setCompany(companyData)
        }

        if (memorialsRes.ok) {
          const memorialsData = await memorialsRes.json()
          setMemorials(memorialsData.slice(0, 5))
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-memorial-primary" />
      </div>
    )
  }

  const statCards = [
    {
      name: 'Gedenkseiten',
      value: stats?.totals.memorials || 0,
      change: `+${stats?.recent.memorialsThisMonth || 0} diesen Monat`,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      name: 'Kondolenzen',
      value: stats?.totals.condolences || 0,
      change: `+${stats?.recent.condolencesThisWeek || 0} diese Woche`,
      icon: MessageCircle,
      color: 'bg-purple-500'
    },
    {
      name: 'Kerzen',
      value: stats?.totals.candles || 0,
      change: `+${stats?.recent.candlesThisWeek || 0} diese Woche`,
      icon: Flame,
      color: 'bg-amber-500'
    },
    {
      name: 'Erinnerungen',
      value: stats?.totals.memories || 0,
      change: 'Geschichten',
      icon: BookOpen,
      color: 'bg-green-500'
    },
  ]

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Willkommen zurück{company ? `, ${company.name}` : ''}
          </p>
        </div>
        <Link
          href="/admin/memorials"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neue Gedenkseite
        </Link>
      </div>

      {/* Statistics */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                  <TrendingUp className="w-3 h-3" />
                  {stat.change}
                </span>
              </div>
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.name}</p>
            </div>
          )
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent memorials */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <h2 className="font-medium text-gray-900">Aktive Gedenkseiten</h2>
            <Link
              href="/admin/memorials"
              className="text-sm text-memorial-primary hover:text-memorial-secondary flex items-center gap-1"
            >
              Alle ansehen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {memorials.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Noch keine Gedenkseiten vorhanden
              </div>
            ) : (
              memorials.map((memorial) => (
                <Link
                  key={memorial.id}
                  href={`/admin/memorials?id=${memorial.id}`}
                  className="p-4 hover:bg-gray-50 transition-colors block"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-memorial-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-memorial-primary font-medium text-sm">
                          {memorial.firstName?.charAt(0)}{memorial.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {memorial.firstName} {memorial.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{memorial.city || 'Kein Ort'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {memorial._count.condolenceComments}
                        </span>
                        <span className="flex items-center gap-1">
                          <Flame className="w-4 h-4" />
                          {memorial._count.candles}
                        </span>
                      </div>
                      {memorial.dateOfDeath && (
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(memorial.dateOfDeath).toLocaleDateString('de-DE')}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <h2 className="font-medium text-gray-900">Aktivität</h2>
            <Activity className="w-4 h-4 text-gray-400" />
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.activity.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Keine aktuelle Aktivität
              </div>
            ) : (
              stats?.activity.slice(0, 8).map((activity, index) => (
                <div key={index} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === 'condolence' ? 'bg-purple-100' : 'bg-amber-100'
                    }`}>
                      {activity.type === 'condolence' && <MessageCircle className="w-4 h-4 text-purple-600" />}
                      {activity.type === 'candle' && <Flame className="w-4 h-4 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {activity.type === 'condolence' && (
                          <><span className="font-medium">{activity.user}</span> hat eine Kondolenz für <span className="font-medium">{activity.memorial}</span> hinterlassen</>
                        )}
                        {activity.type === 'candle' && (
                          <><span className="font-medium">{activity.user}</span> hat eine Kerze für <span className="font-medium">{activity.memorial}</span> angezündet</>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true, locale: de })}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-memorial-primary to-memorial-primary/80 rounded-xl p-5 text-white">
          <Calendar className="w-8 h-8 mb-3 opacity-80" />
          <p className="text-2xl font-semibold">{stats?.recent.memorialsThisMonth || 0}</p>
          <p className="text-white/80 text-sm">Gedenkseiten diesen Monat</p>
        </div>
        <div className="bg-gradient-to-br from-memorial-secondary to-memorial-secondary/80 rounded-xl p-5 text-white">
          <Building2 className="w-8 h-8 mb-3 opacity-80" />
          <p className="text-2xl font-semibold">{stats?.totals.institutions || 0}</p>
          <p className="text-white/80 text-sm">Verknüpfte Institutionen</p>
        </div>
        <div className="bg-gradient-to-br from-memorial-accent to-amber-500 rounded-xl p-5 text-white">
          <Users className="w-8 h-8 mb-3 opacity-80" />
          <p className="text-2xl font-semibold">{stats?.totals.familyAccounts || 0}</p>
          <p className="text-white/80 text-sm">Aktive Familienkonten</p>
        </div>
      </div>
    </div>
  )
}
